import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import {
  DndContext,
  closestCenter,
  KeyboardSensor,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import type { DragEndEvent } from '@dnd-kit/core';
import {
  arrayMove,
  SortableContext,
  sortableKeyboardCoordinates,
  useSortable,
  verticalListSortingStrategy,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { GripVertical } from 'lucide-react';

export interface TabConfig {
  id: string;
  label: string;
  IconComponent: React.ComponentType<{ className?: string }>;
}

interface TabSettingsDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  availableTabs: TabConfig[];
  selectedTabIds: string[];
  onSave: (tabIds: string[]) => void;
  visibleTabCount: number;
}

interface SortableTabItemProps {
  tab: TabConfig;
  index: number;
  isTopFive: boolean;
  isOverview: boolean;
}

function SortableTabItem({ tab, index, isTopFive, isOverview }: SortableTabItemProps) {
  const {
    attributes,
    listeners,
    setNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: tab.id, disabled: isOverview });

  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.5 : 1,
  };

  const Icon = tab.IconComponent;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={`flex items-center gap-3 p-3 rounded-lg border transition-colors ${
        isOverview
          ? 'bg-blue-50 dark:bg-blue-950/20 border-blue-200 dark:border-blue-800'
          : isTopFive
          ? 'bg-teal-50 dark:bg-teal-950/20 border-teal-200 dark:border-teal-800'
          : 'bg-muted border-border'
      }`}
    >
      <div
        {...attributes}
        {...listeners}
        className={`${isOverview ? 'invisible' : 'cursor-grab active:cursor-grabbing'}`}
      >
        <GripVertical className="h-4 w-4 text-neutral-400" />
      </div>
      
      <div className="flex items-center gap-2 flex-1">
        {Icon && <Icon className="h-4 w-4" />}
        <span className="text-sm font-medium">{tab.label}</span>
      </div>
    </div>
  );
}

export function TabSettingsDialog({
  open,
  onOpenChange,
  availableTabs,
  selectedTabIds,
  onSave,
  visibleTabCount,
}: TabSettingsDialogProps) {
  const [orderedTabs, setOrderedTabs] = useState<TabConfig[]>([]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  useEffect(() => {
    if (open && availableTabs && availableTabs.length > 0) {
      // Create ordered list: Overview first, then selected tabs, then remaining tabs
      const overviewTab = availableTabs.find((t) => t.id === 'overview');
      const selectedTabs = selectedTabIds
        .map((id) => availableTabs.find((t) => t.id === id))
        .filter((t): t is TabConfig => t !== undefined && t.id !== 'overview');
      const remainingTabs = availableTabs.filter(
        (t) => t.id !== 'overview' && !selectedTabIds.includes(t.id)
      );

      const ordered = [
        ...(overviewTab ? [overviewTab] : []),
        ...selectedTabs,
        ...remainingTabs,
      ];
      setOrderedTabs(ordered);
    }
  }, [open, availableTabs, selectedTabIds]);

  const handleDragEnd = (event: DragEndEvent) => {
    const { active, over } = event;

    if (over && active.id !== over.id) {
      setOrderedTabs((items) => {
        const oldIndex = items.findIndex((item) => item.id === active.id);
        const newIndex = items.findIndex((item) => item.id === over.id);

        // Don't allow moving overview tab
        if (items[oldIndex].id === 'overview') {
          return items;
        }

        // Don't allow moving items before overview
        if (newIndex === 0) {
          return items;
        }

        return arrayMove(items, oldIndex, newIndex);
      });
    }
  };

  const handleSave = () => {
    // Save the order (excluding overview which is always first)
    const tabIds = orderedTabs
      .filter((t) => t.id !== 'overview')
      .map((t) => t.id);
    onSave(tabIds);
    onOpenChange(false);
  };

  const handleReset = () => {
    // Reset to default order
    if (availableTabs.length > 0) {
      const overviewTab = availableTabs.find((t) => t.id === 'overview');
      const otherTabs = availableTabs.filter((t) => t.id !== 'overview');
      const ordered = [
        ...(overviewTab ? [overviewTab] : []),
        ...otherTabs,
      ];
      setOrderedTabs(ordered);
    }
  };

  const topFiveCount = Math.min(visibleTabCount, orderedTabs.length);

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-2xl max-h-[80vh] flex flex-col">
        <DialogHeader>
          <DialogTitle>Configure Tabs</DialogTitle>
          <DialogDescription className="text-sm">
            Drag and drop to reorder tabs. The first {visibleTabCount} tabs (teal) are currently visible in the tab bar based on your screen size. The rest (gray) will appear in the "More" dropdown.
          </DialogDescription>
        </DialogHeader>

        <div className="flex-1 overflow-y-auto pr-2">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedTabs.map((t) => t.id)}
              strategy={verticalListSortingStrategy}
            >
              <div className="space-y-2">
                {orderedTabs.length === 0 && (
                  <div className="flex items-center justify-center h-32 text-sm text-neutral-500">
                    Loading tabs...
                  </div>
                )}
                {orderedTabs.map((tab, index) => {
                  const showTopFiveLabel = index === 1; // Show after Overview
                  const showMoreLabel = index === visibleTabCount;
                  return (
                    <React.Fragment key={tab.id}>
                      {showTopFiveLabel && (
                        <div className="flex items-center gap-2 mt-4 mb-2">
                          <div className="h-px flex-1 bg-teal-200 dark:bg-teal-800"></div>
                          <span className="text-xs font-semibold text-teal-600 dark:text-teal-400 uppercase tracking-wider">
                            Visible Tabs ({visibleTabCount})
                          </span>
                          <div className="h-px flex-1 bg-teal-200 dark:bg-teal-800"></div>
                        </div>
                      )}
                      {showMoreLabel && (
                        <div className="flex items-center gap-2 my-4">
                          <div className="h-px flex-1 bg-neutral-300 dark:bg-neutral-700"></div>
                          <span className="text-xs font-semibold text-neutral-500 dark:text-neutral-400 uppercase tracking-wider">
                            More Dropdown ({orderedTabs.length - visibleTabCount} {orderedTabs.length - visibleTabCount === 1 ? 'Tab' : 'Tabs'})
                          </span>
                          <div className="h-px flex-1 bg-neutral-300 dark:bg-neutral-700"></div>
                        </div>
                      )}
                      <SortableTabItem
                        tab={tab}
                        index={index}
                        isTopFive={index < topFiveCount}
                        isOverview={tab.id === 'overview'}
                      />
                    </React.Fragment>
                  );
                })}
              </div>
            </SortableContext>
          </DndContext>
        </div>

        <DialogFooter className="flex-row justify-between items-center gap-2 sm:justify-between">
          <Button variant="outline" onClick={handleReset} className="text-sm">
            Reset to Default
          </Button>
          <div className="flex gap-2">
            <Button variant="outline" onClick={() => onOpenChange(false)} className="text-sm">
              Cancel
            </Button>
            <Button onClick={handleSave} className="text-sm">
              Save Changes
            </Button>
          </div>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
